import { gqlErrorCode, gqlErrorData } from '@/lib/gql_error'

/*
 * Signing a proposal: words for the server's delivery states (ENT-565 ZDP-T39, release R4).
 *
 * The server owns every fact: whether the proposal may be sent, which state the signature request is
 * in, whether the client was emailed. This module only turns the server's state NAMES into words and
 * decides which words may be shown with which. It computes nothing and contains no vendor name.
 *
 * Two different facts are kept apart on purpose:
 *   1. the SIGNATURE REQUEST (delivery state): was the request made, is the client signing, did they sign;
 *   2. the CLIENT NOTICE (client notice state): did the platform email the client the signing link.
 * A hand-off to the notification service is not a delivered email, so the notice line never says
 * "sent" unless the server reports EMAIL_SENT, and never says anything positive for a request that is
 * not confirmed.
 *
 * Contract: entity-backend docs/handover/fe/zoho-driven-proposals/r4/README.md. Errors are read by
 * NAME and `data.reason`, never by `error.message` (graphql-request builds that message from the
 * request variables). Nothing here, and nothing this screen sends or stores, is a signing link, a
 * client name or a client email: the contract returns none.
 */

export const DELIVERY_STATES = [
  'REQUESTING_SIGNATURE',
  'AWAITING_SIGNATURE',
  'SIGNED',
  'DECLINED',
  'EXPIRED',
  'REQUEST_FAILED',
  'REQUEST_UNCONFIRMED',
  'SUPERSEDED',
  'VOIDED',
] as const
export type DeliveryState = (typeof DELIVERY_STATES)[number]

export type SignatureTone = 'neutral' | 'progress' | 'info' | 'success' | 'warning' | 'danger'

export interface DeliveryStateText {
  /** False for a state this build does not know: neutral words, never success. */
  known: boolean
  label: string
  detail: string
  tone: SignatureTone
}

const DELIVERY_TEXT: Record<DeliveryState, DeliveryStateText> = {
  REQUESTING_SIGNATURE: {
    known: true,
    label: 'Requesting signature',
    detail: 'The signature request is being made. It is not confirmed yet, so the client has not been asked to sign.',
    tone: 'progress',
  },
  AWAITING_SIGNATURE: {
    known: true,
    label: 'Waiting for the client to sign',
    detail: 'The signature request is confirmed. The client has not signed yet.',
    tone: 'info',
  },
  SIGNED: {
    known: true,
    label: 'Signed',
    detail: 'The client signed the proposal.',
    tone: 'success',
  },
  DECLINED: {
    known: true,
    label: 'Declined',
    detail: 'The client declined to sign. A decline is a change request: revise the proposal to send a new version. A declined request cannot be sent again.',
    tone: 'warning',
  },
  EXPIRED: {
    known: true,
    label: 'Expired',
    detail: 'The signing period ended before the client signed. This signature request is closed and cannot be sent again.',
    tone: 'warning',
  },
  REQUEST_FAILED: {
    known: true,
    label: 'Request failed',
    detail: 'The signature request could not be made. The client was not asked to sign.',
    tone: 'danger',
  },
  REQUEST_UNCONFIRMED: {
    known: true,
    label: 'Request not confirmed',
    detail: 'It is not known whether the signature request was made. Check again before doing anything else.',
    tone: 'warning',
  },
  SUPERSEDED: {
    known: true,
    label: 'Replaced',
    detail: 'A newer version replaced this proposal, so this signature request is closed.',
    tone: 'neutral',
  },
  // The signing service cancelled the request (found by the server's 15-minute check, no event). Nobody signed or
  // declined, so this is neither a success nor the client's answer. 'warning', as EXPIRED: a closed request that
  // needs a person to send again. 'neutral' (SUPERSEDED) is for a closure that is the staff's own doing and needs nothing.
  VOIDED: {
    known: true,
    label: 'Withdrawn from the signing service',
    detail: 'The signature request was cancelled before the client signed. Nothing was signed or declined. Send the proposal again to ask for a new signature.',
    tone: 'warning',
  },
}

const UNKNOWN_DELIVERY: DeliveryStateText = {
  known: false,
  label: 'Unknown',
  detail: 'The server reported a signing status this screen does not know. Nothing is confirmed.',
  tone: 'neutral',
}

/** The words for a delivery state; an unknown state is a neutral "Unknown", never hidden and never a success. */
export function deliveryStateText(state: string | null | undefined): DeliveryStateText {
  return (state && (DELIVERY_TEXT as Record<string, DeliveryStateText>)[state]) || UNKNOWN_DELIVERY
}

/**
 * The states in which the signature request is confirmed to exist: awaiting the client, or later. VOIDED is
 * one: a request can only be cancelled at the signing service if it was made there.
 */
const CONFIRMED_REQUEST_STATES: readonly string[] = ['AWAITING_SIGNATURE', 'SIGNED', 'DECLINED', 'EXPIRED', 'VOIDED']

export function isRequestConfirmed(state: string | null | undefined): boolean {
  return !!state && CONFIRMED_REQUEST_STATES.includes(state)
}

/** True when the screen may use success colour or wording: only for a signed proposal. */
export function mayShowSuccess(state: string | null | undefined): boolean {
  return state === 'SIGNED'
}

// ── The toast after a send or a retry ───────────────────────────────────────────────────────────

export type SignatureToastTone = 'success' | 'info' | 'warning' | 'error'
export interface SignatureToast {
  tone: SignatureToastTone
  text: string
}
export type SignatureAction = 'SEND' | RetryMode

/**
 * What to tell the person after a send or a retry, from the state the SERVER answered with, never from
 * the fact that the call returned. The server answers a send before the signing service has, so a
 * returned call is often a request that is still being made, or one that has already failed. A success
 * tone is for a state that means the request exists (waiting for the client) or the client signed; the
 * rest follows the delivery state's own tone (VOIDED is a warning, as in `DELIVERY_TEXT`).
 */
export function signatureOutcomeToast(action: SignatureAction, state: string | null | undefined, replayed: boolean): SignatureToast {
  const again = action === 'RESEND'
  switch (state) {
    case 'AWAITING_SIGNATURE':
      if (action === 'RECHECK') return { tone: 'success', text: 'Status checked. The request is confirmed; waiting for the client to sign.' }
      if (replayed) return { tone: 'success', text: again ? 'A new request is already in progress' : 'A signature request is already in progress' }
      return { tone: 'success', text: again ? 'New signature request made. Waiting for the client to sign.' : 'Signature request made. Waiting for the client to sign.' }
    case 'SIGNED':
      return { tone: 'success', text: 'Status checked. The client signed the proposal.' }
    case 'REQUESTING_SIGNATURE':
      if (action === 'RECHECK') return { tone: 'info', text: 'Status checked. The request is still being made.' }
      if (replayed) return { tone: 'info', text: `${again ? 'A new request' : 'A signature request'} is already in progress. It is not confirmed yet.` }
      return { tone: 'info', text: `${again ? 'New signature request' : 'Signature request'} started. It is not confirmed yet.` }
    case 'REQUEST_FAILED':
      return { tone: 'error', text: 'The signature request failed. The client was not asked to sign.' }
    case 'REQUEST_UNCONFIRMED':
      return { tone: 'warning', text: 'It is not known whether the signature request was made. Check again before doing anything else.' }
    default: {
      const t = deliveryStateText(state)
      if (!t.known) return { tone: 'info', text: 'The signing status is not one this screen knows. Nothing is confirmed.' }
      return { tone: t.tone === 'warning' ? 'warning' : 'info', text: `Signature request: ${t.label}.` }
    }
  }
}

// ── The signer in the send confirmation ─────────────────────────────────────────────────────────

/**
 * An email address with all but the first character of each part hidden: `amira@clientco.example` is
 * `a***@c***.example`. Null when the input is not shaped like an address, so that a malformed value is
 * never echoed back. The full address is never an output of this function.
 */
export function maskEmail(email: string | null | undefined): string | null {
  if (typeof email !== 'string') return null
  const parts = email.split('@')
  if (parts.length !== 2 || /\s/.test(email)) return null
  const [local, domain] = parts
  const labels = domain.split('.')
  if (local === '' || labels.length < 2 || labels.some((l) => l === '')) return null
  return `${local[0]}***@${labels[0][0]}***.${labels[labels.length - 1]}`
}

// ── The client notice (the platform's own email to the client) ──────────────────────────────────

export const CLIENT_NOTICE_STATES = ['NOT_SENT', 'NOTIFY_ACCEPTED', 'EMAIL_SENT', 'EMAIL_FAILED', 'EMAIL_UNCONFIRMED'] as const
export type ClientNoticeState = (typeof CLIENT_NOTICE_STATES)[number]

export interface ClientNoticeText {
  known: boolean
  label: string
  detail: string
  tone: SignatureTone
}

const NOTICE_TEXT: Record<ClientNoticeState, ClientNoticeText> = {
  NOT_SENT: {
    known: true,
    label: 'Not sent',
    detail: 'No email with the signing link has been handed over yet.',
    tone: 'neutral',
  },
  NOTIFY_ACCEPTED: {
    known: true,
    label: 'Handed to the notification service',
    detail: 'The notification service accepted the email. That is not a confirmation that the client received it: until the email is confirmed, do not assume the client has the signing link.',
    tone: 'info',
  },
  EMAIL_SENT: {
    known: true,
    label: 'Email sent',
    detail: 'The notification service confirmed that the email with the signing link was sent to the client.',
    tone: 'success',
  },
  EMAIL_FAILED: {
    known: true,
    label: 'Email failed',
    detail: 'The mail service did not take the email with the signing link, and it is not sent again automatically. The client has not received it.',
    tone: 'danger',
  },
  EMAIL_UNCONFIRMED: {
    known: true,
    label: 'Email not confirmed',
    detail: 'It is not known whether the email was sent. Do not assume the client received it.',
    tone: 'warning',
  },
}

const UNKNOWN_NOTICE: ClientNoticeText = {
  known: false,
  label: 'Unknown',
  detail: 'The server reported an email status this screen does not know. Nothing is confirmed.',
  tone: 'neutral',
}

/** A notice state that says the email went, or is on its way, and so needs a confirmed request behind it. */
const POSITIVE_NOTICE: readonly string[] = ['NOTIFY_ACCEPTED', 'EMAIL_SENT']

const INCONSISTENT_NOTICE: ClientNoticeText = {
  known: false,
  label: 'Not confirmed',
  detail: 'The signature request is not confirmed, so no email to the client is confirmed either.',
  tone: 'neutral',
}

/**
 * The words for the client-notice line, given the request's state. A positive notice (handed over,
 * sent) is shown only when the request itself is confirmed: otherwise the line says nothing is
 * confirmed, so the screen cannot claim the client was emailed for a request that does not stand.
 */
export function clientNoticeText(notice: string | null | undefined, deliveryState: string | null | undefined): ClientNoticeText {
  const text = (notice && (NOTICE_TEXT as Record<string, ClientNoticeText>)[notice]) || UNKNOWN_NOTICE
  if (notice && POSITIVE_NOTICE.includes(notice) && !isRequestConfirmed(deliveryState)) return INCONSISTENT_NOTICE
  return text
}

// ── Retry ────────────────────────────────────────────────────────────────────────────────────────

export type RetryMode = 'RESEND' | 'RECHECK'

/**
 * What the retry may do in this state: RESEND only from REQUEST_FAILED, RECHECK from
 * REQUEST_UNCONFIRMED or REQUESTING_SIGNATURE, nothing from DECLINED (the contract's table). The
 * server's own flags (`canResend`, `canRecheck`) win when the caller passes them as `serverAllowed`;
 * the server still decides on every call.
 */
export function retryModes(state: string | null | undefined, serverAllowed?: readonly string[] | null): RetryMode[] {
  const fromServer = serverAllowed?.filter((m): m is RetryMode => m === 'RESEND' || m === 'RECHECK')
  if (serverAllowed) return fromServer ?? []
  if (state === 'REQUEST_FAILED') return ['RESEND']
  if (state === 'REQUEST_UNCONFIRMED' || state === 'REQUESTING_SIGNATURE') return ['RECHECK']
  return []
}

export const RETRY_LABEL: Record<RetryMode, string> = {
  RESEND: 'Send again',
  RECHECK: 'Check again',
}

/** What each retry does, in words (shown before the click). */
export const RETRY_EXPLANATION: Record<RetryMode, string> = {
  RESEND: 'Sending again sends the client a new email with a new signing link. The earlier link may expire and stop working.',
  RECHECK: 'Checking again asks the signing service whether the request was made. It does not send another email.',
}

// ── Size ─────────────────────────────────────────────────────────────────────────────────────────

/** A byte count in words, with the exact count kept so the figure can be checked against the stored one. */
export function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return 'Unknown size'
  if (bytes < 1024) return `${bytes} bytes`
  const kib = bytes / 1024
  if (kib < 1024) return `${kib.toFixed(1)} KB (${bytes} bytes)`
  return `${(kib / 1024).toFixed(1)} MB (${bytes} bytes)`
}

/**
 * States in which the proposal can be sent (APPROVED), has been signed (ACCEPTED) or has run out (EXPIRED: the
 * signing period lapsed, or the offer date passed, and the server closed the proposal). Any other state has no signing panel.
 */
export function showsSignaturePanel(state: string): boolean {
  return state === 'APPROVED' || state === 'ACCEPTED' || state === 'EXPIRED'
}

// ── Polling ──────────────────────────────────────────────────────────────────────────────────────

/**
 * How long to wait before reading the signing status again, or false to stop. The contract suggests
 * every 3 s for 30 s, then every 15 s, until the state moves. A request waiting for the client is
 * read slowly (the client may sign, or the mail service may report), and a state that only a person
 * can move (failed, signed, declined, expired, replaced) is not polled at all.
 */
export function signaturePollInterval(
  delivery: { state: string; updatedAt: string } | null | undefined,
  now: number = Date.now(),
  /** The server's `isReadOnly`. Only an explicit `false` next to a SIGNED delivery means "acceptance is still being recorded". */
  proposalReadOnly?: boolean,
): number | false {
  if (!delivery) return false
  switch (delivery.state) {
    case 'REQUESTING_SIGNATURE': {
      const since = Date.parse(delivery.updatedAt)
      return Number.isFinite(since) && now - since < 30_000 ? 3_000 : 15_000
    }
    case 'REQUEST_UNCONFIRMED':
      return 15_000
    case 'AWAITING_SIGNATURE':
      return 30_000
    // The delivery is SIGNED but the proposal does not read ACCEPTED yet: the server finishes the second move
    // within seconds (at most ~15 minutes after a fault). Read again until it does, so the record locks.
    case 'SIGNED':
      return proposalReadOnly === false ? 5_000 : false
    default:
      return false
  }
}

// ── The executed proposal (delivery SIGNED) and the read-only record (T25 additions) ────────────

/** What a hash is, in words, next to the shortened figure. */
export const INTEGRITY_HASH_NOTE = 'The fingerprint of the executed document, to check a copy of it against.'

/** A hash shortened for reading; the whole value is what gets copied. Anything too short to shorten is shown as it is. */
export function shortHash(hash: string): string {
  return hash.length > 20 ? `${hash.slice(0, 8)}\u2026${hash.slice(-8)}` : hash
}

/**
 * The read-only record. `isReadOnly` is the server's fact (the proposal is ACCEPTED). The same locks
 * apply in the short window where the delivery already reads SIGNED but the proposal does not read
 * ACCEPTED yet: the server refuses Revise there (`signature_received`), so it is not offered either.
 */
export function isRecordLocked(signature: { isReadOnly?: boolean | null; delivery?: { state: string } | null } | null | undefined): boolean {
  return signature?.isReadOnly === true || signature?.delivery?.state === 'SIGNED'
}

/** True while the delivery is SIGNED and the proposal is not ACCEPTED yet: the acceptance is being recorded. */
export function isAcceptancePending(signature: { isReadOnly?: boolean | null; delivery?: { state: string } | null } | null | undefined): boolean {
  return signature?.delivery?.state === 'SIGNED' && signature.isReadOnly !== true
}

/**
 * True when the proposal itself is EXPIRED: a terminal, read-only state the server reaches when a signature request
 * lapses (the second of two moves, as for SIGNED). It is NOT `isReadOnly`, which means ACCEPTED and carries the
 * accepted notice: read `proposalState`. The server refuses every write on it, Revise included.
 */
export function isProposalExpired(signature: { proposalState?: string | null } | null | undefined): boolean {
  return signature?.proposalState === 'EXPIRED'
}

export const EXPIRED_NOTICE = 'This proposal has expired. It is a read-only record: it cannot be revised, edited or sent for signature. To offer this deal again, create a new proposal for it.'
export const READ_ONLY_NOTICE = 'This proposal is accepted. It is a read-only record: it cannot be revised or edited.'
export const ACCEPTANCE_PENDING_NOTICE = 'The client has signed. The proposal is being marked as accepted, so it can no longer be revised.'

export interface SignerSummary {
  contact: string
  identity: string
  /** False when the contact had already been erased at signing: nothing was stored to check the signer against. */
  identityRecorded: boolean
}

/**
 * Who signed, as far as the contract says. It returns no name and no email: the signer is a client
 * contact RECORD, whose values are behind the page's own audited "Show client contact" action.
 */
export function signerSummary(signed: { signerContactId?: string | null; signerIdentityRecorded: boolean }): SignerSummary {
  return {
    contact: signed.signerContactId
      ? 'The client contact recorded for this proposal. To see who that is, use Show client contact; showing it is recorded against your user.'
      : 'No client contact record is linked to this signature.',
    identity: signed.signerIdentityRecorded
      ? "The signer's identity proof was stored. It is kept as hashes, never as the name or email."
      : 'Signer details erased. The contact had already been erased when the signature arrived, so no identity proof could be stored.',
    identityRecorded: signed.signerIdentityRecorded,
  }
}

export type DeclineReasonView =
  | { kind: 'GIVEN'; text: string }
  | { kind: 'NOT_GIVEN'; text: string }
  | { kind: 'UNAVAILABLE'; text: string }

/**
 * The client's reason for declining, by the server's `reasonStatus`. The text is free text from a
 * third party: it is returned for the caller to render as plain text (a React text node), never as
 * HTML. A status this build does not know shows no reason and says so, and offers the refresh.
 */
export function declineReasonView(declined: { reasonStatus: string; reason?: string | null }): DeclineReasonView {
  switch (declined.reasonStatus) {
    case 'GIVEN':
      return typeof declined.reason === 'string' && declined.reason.trim() !== ''
        ? { kind: 'GIVEN', text: declined.reason }
        : { kind: 'NOT_GIVEN', text: 'No reason given' }
    case 'NOT_GIVEN':
      return { kind: 'NOT_GIVEN', text: 'No reason given' }
    default:
      return { kind: 'UNAVAILABLE', text: 'Reason temporarily unavailable' }
  }
}

// ── What the Send action says when it is not offered ─────────────────────────────────────────────

/**
 * Text for a proposal that cannot be sent right now, by the server's `sendBlockedReason`; null means
 * say nothing (the delivery's own status is the answer, or the proposal is not at this stage).
 * An unknown reason gets a plain line and never the raw name.
 */
const BLOCKED_TEXT: Record<string, string | null> = {
  NOT_APPROVED: null,
  ALREADY_REQUESTED: null,
  ALREADY_SIGNED: null,
  NO_DOCUMENT: 'Render the proposal first.',
  DECLINED_REVISE_REQUIRED: 'The client declined this version. Revise the proposal to send a new one.',
  DELIVERY_ENDED: 'The signature request for this version has ended. Revise the proposal to send a new one.',
  VALIDITY_EXPIRED: 'The offer has expired.',
  // Owner decision 2026-10-28: say nothing about why.
  HAS_DRAFT_WORDING: 'This proposal cannot be sent to a client yet.',
  SIGNATURE_DISABLED: 'Sending for signature is not enabled.',
}

export function sendBlockedText(reason: string | null | undefined): string | null {
  if (!reason) return null
  return reason in BLOCKED_TEXT ? BLOCKED_TEXT[reason] : 'This proposal cannot be sent for signature right now.'
}

/** Why a request failed (`failureReason`), in words. */
const FAILURE_REASON_TEXT: Record<string, string> = {
  NO_VALID_RECIPIENT: 'The client has no valid email address on this proposal, so nothing was sent. Correct the client contact in a new round (Revise); sending again will not help until then.',
  ATTEMPTS_EXHAUSTED: 'The signing service did not accept the request after several attempts. Nothing was sent to the client.',
  REQUEST_REFUSED: 'The signing service refused the request. Nothing was sent to the client.',
}

export function failureReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null
  return FAILURE_REASON_TEXT[reason] ?? 'The signature request failed. Nothing was sent to the client.'
}

// ── Refusals of send and retry, by the server's error name ───────────────────────────────────────

export type SignatureFailureKind = 'NOT_ALLOWED' | 'NOT_SENDABLE' | 'CONFLICT' | 'NOT_FOUND' | 'UNAVAILABLE' | 'NOT_PERMITTED' | 'INVALID' | 'GENERAL'

export interface SignatureFailure {
  kind: SignatureFailureKind
  /** The server's error name, shown with the words; null when the server never answered. */
  name: string | null
  message: string
  /** The SAME requestId must be sent again: the server never answered. */
  keepRequestId: boolean
  /** The status should be re-read before anything else is tried. */
  reload: boolean
}

const REASON_WORDS: Record<string, string> = {
  RESEND_NOT_FROM_DECLINED: 'A declined proposal cannot be sent again. Revise the proposal to send a new one.',
  RESEND_ONLY_FROM_REQUEST_FAILED: 'Sending again is only possible after a request has failed.',
  RECHECK_NOT_AVAILABLE: 'Checking again is only possible while the request is not confirmed.',
  NOT_APPROVED: 'The proposal is no longer approved (it may have been revised or withdrawn). Reload the page.',
}

const make = (kind: SignatureFailureKind, name: string | null, message: string, extra: Partial<Pick<SignatureFailure, 'keepRequestId' | 'reload'>> = {}): SignatureFailure => ({
  kind,
  name,
  message,
  keepRequestId: extra.keepRequestId ?? false,
  reload: extra.reload ?? false,
})

export function signatureFailure(err: unknown): SignatureFailure {
  const code = gqlErrorCode(err)
  const data = gqlErrorData(err)
  switch (code) {
    case 'CRM_PROPOSAL_CLAUSE_NOT_APPROVED':
      // No detail by design: the server sends none and this screen asks for none.
      return make('NOT_SENDABLE', code, 'This proposal cannot be sent to a client yet.', { reload: true })
    case 'CRM_PROPOSAL_SIGNATURE_DISABLED':
      return make('NOT_SENDABLE', code, 'Sending for signature is not enabled.', { reload: true })
    case 'CRM_PROPOSAL_VALIDITY_EXPIRED':
      return make('NOT_SENDABLE', code, 'The offer has expired, so it cannot be sent.', { reload: true })
    case 'CRM_PROPOSAL_SIGNATURE_PROVIDER_UNAVAILABLE':
      return make('UNAVAILABLE', code, 'The signing service did not answer. Nothing was changed. Try again later.')
    case 'CRM_TRANSITION_NOT_ALLOWED': {
      const reason = typeof data?.reason === 'string' ? data.reason : null
      const words = reason ? (REASON_WORDS[reason] ?? (reason in BLOCKED_TEXT ? BLOCKED_TEXT[reason] : null)) : null
      return make('NOT_ALLOWED', code, words ?? 'This is not possible in the current state of the signature request.', { reload: true })
    }
    case 'CRM_VERSION_CONFLICT':
      return make('CONFLICT', code, 'The signature request changed while you were working on it. The status has been reloaded; check it, then try again.', { reload: true })
    case 'CRM_RECORD_NOT_FOUND':
      return make('NOT_FOUND', code, 'This proposal or signature request was not found. It may belong to another organisation or have been removed.')
    case 'CRM_PROPOSAL_INPUT_INVALID':
      return make('INVALID', code, 'The server refused this request. Try again.')
    case 'FORBIDDEN':
      return make('NOT_PERMITTED', code, 'You do not have permission to send proposals for signature.')
    case 'CRM_NOT_ENTITLED':
      return make('NOT_PERMITTED', code, 'Proposals are not enabled for your organisation.')
    case 'CRM_ACTOR_MISSING':
      return make('NOT_PERMITTED', code, 'Your user is not set up for proposals yet. Ask an administrator.')
    default:
      return code
        ? make('GENERAL', /^[A-Z0-9_]{1,64}$/.test(code) ? code : null, 'The server refused the request. Nothing is known to have changed; check the status before trying again.', { reload: true })
        : make('GENERAL', null, 'The server did not answer. It is not known whether the request was made; check the status before trying again.', { keepRequestId: true, reload: true })
  }
}

/** The failure as one line: the words, then the server's error name. */
export function signatureFailureText(f: SignatureFailure): string {
  return f.name === null ? f.message : `${f.message} (${f.name})`
}
